import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import {
  loginPostApi,
  creatorForgotPasswordApi,
  creatorVerifyOtpApi,
  creatorResetPasswordApi,
  creatorPublicSupportCategories,
  creatorGuestSupportRequest,
} from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import { faqsForTopic } from "../CreatorFaq/faqData";
import RelatedFaqs from "../CreatorFaq/RelatedFaqs";
import { landingRouteForAdmin } from "../login/Login";
import { safeNextPath } from "../../utils/postLoginRedirect";
import { useTenant } from "../../context/TenantContext";
import "./CreatorLogin.css";

// Brand name shown on the page, taken from the resolved tenant so every
// tenant's creator login carries its own name rather than a hardcoded one.
const useBrandName = () => useTenant()?.tenant?.name || "the platform";

// Attachments on the Contact Support form. Mirror the backend's
// supportAttachments middleware (same limits as the signed-in CreatorSupport form).
const ATTACH_MAX_FILES = 3;
const ATTACH_MAX_MB = 10; // images and PDFs
const ATTACH_VIDEO_MAX_MB = 100;
const ATTACH_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];
const ATTACH_TYPES = ["image/jpeg", "image/png", "application/pdf", ...ATTACH_VIDEO_TYPES];
const isVideo = (file) => ATTACH_VIDEO_TYPES.includes(file.type);
const fmtSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const PERKS = [
  {
    key: "pool",
    title: "Watch Pool",
    body: "A monthly share, by minutes watched",
    d: <><path d="M3 7c3 0 3 3 6 3s3-3 6-3 3 3 6 3" /><path d="M3 13c3 0 3 3 6 3s3-3 6-3 3 3 6 3" /></>,
  },
  {
    key: "ref",
    title: "Referral link",
    body: "Credit for every sign-up it brings",
    d: <><path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" /></>,
  },
  {
    key: "pay",
    title: "Monthly payouts",
    body: "Views, watch time and Stripe payouts",
    d: <><path d="M3 3v18h18" /><path d="m7 14 4-4 3 3 5-6" /></>,
  },
  {
    key: "access",
    title: "Your access, your call",
    body: "Free or Subscription, per title",
    d: <><path d="M3 7l9-4 9 4-9 4-9-4Z" /><path d="M3 12l9 4 9-4M3 17l9 4 9-4" /></>,
  },
];

function describeError(error, fallback) {
  const response = error?.response?.data;
  if (response?.errors) {
    const fieldErrors = Object.values(response.errors).flat();
    if (fieldErrors.length > 0) return fieldErrors[0];
  }
  if (response?.message) return String(response.message);
  if (error?.message) return error.message;
  return fallback;
}

function SignInForm({ onForgotPassword }) {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const brandName = useBrandName();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogin = async () => {
    if (!formData.email || !formData.password) {
      showToast("Email and password are required.", "warning");
      return;
    }
    try {
      setLoading(true);
      const response = await loginPostApi({
        email: formData.email,
        password: formData.password,
      });
      const role = response?.data?.data?.user?.role;
      const token = response?.data?.data?.token;
      localStorage.setItem("userInfo", JSON.stringify(response?.data?.data));
      const permissions = response?.data?.data?.user?.permissions || {};
      localStorage.setItem("permissions", JSON.stringify(permissions));
      const refreshToken = response?.data?.data?.refresh_token;
      const expireDate = response?.data?.data?.expire_date;
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("expireDate", expireDate);

      if (role === "Creator" || role === "Admin") {
        localStorage.setItem("role", role);
        localStorage.setItem("token", token);
        window.dispatchEvent(new Event("storage"));
        navigate(
          role === "Creator"
            ? safeNextPath(location.search) || "/mainlayout/creator-dashboard"
            : landingRouteForAdmin(permissions)
        );
      }
      setLoading(false);
    } catch (error) {
      showToast(
        describeError(error, "Something went wrong. Please try again."),
        "error"
      );
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <>
      <span className="cl-kicker cl-kicker-form"><span className="cl-dot" /> Welcome back</span>
      <h2>Sign in</h2>
      <p className="cl-flow-sub">
        Access your titles, referral link and earnings.
      </p>

      <label className="cl-label" htmlFor="cl-email">
        Email address
      </label>
      <input
        id="cl-email"
        className="cl-input"
        type="text"
        name="email"
        placeholder="you@example.com"
        autoFocus
        autoComplete="email"
        value={formData.email}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />

      <div className="cl-password-row">
        <label className="cl-label" htmlFor="cl-password">
          Password
        </label>
        <button
          type="button"
          className="cl-link-btn cl-forgot-link"
          onClick={onForgotPassword}
        >
          Forgot your password?
        </button>
      </div>
      <div className="cl-password-wrap">
        <input
          id="cl-password"
          className="cl-input"
          type={showPassword ? "text" : "password"}
          name="password"
          placeholder="••••••••"
          autoComplete="current-password"
          value={formData.password}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <span
          className="cl-eye"
          onClick={() => setShowPassword((prev) => !prev)}
        >
          {showPassword ? <FaEyeSlash /> : <FaEye />}
        </span>
      </div>

      <button
        className={`cl-btn cl-btn-primary cl-submit${loading ? " loading" : ""}`}
        onClick={handleLogin}
        disabled={loading}
      >
        {loading ? <span className="cl-loader" /> : "Sign in securely"}
      </button>

      <p className="cl-apply">
        New to {brandName}?{" "}
        <Link to="/creator-signup">Join {brandName}</Link>
      </p>
    </>
  );
}

function ForgotPasswordFlow({ onDone, onCancel }) {
  const [step, setStep] = useState("request"); // request -> verify -> reset
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleRequestOtp = async () => {
    if (!email) {
      showToast("Enter your email address.", "warning");
      return;
    }
    try {
      setLoading(true);
      await creatorForgotPasswordApi(email);
      showToast("A verification code has been sent to your email.", "success");
      setStep("verify");
    } catch (error) {
      showToast(
        describeError(error, "Couldn't send a code. Please try again."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      showToast("Enter the code we sent you.", "warning");
      return;
    }
    try {
      setLoading(true);
      await creatorVerifyOtpApi(email, otp);
      setStep("reset");
    } catch (error) {
      showToast(
        describeError(error, "That code didn't work. Please try again."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      showToast("Enter and confirm your new password.", "warning");
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast("Passwords do not match.", "warning");
      return;
    }
    try {
      setLoading(true);
      await creatorResetPasswordApi(email, otp, newPassword);
      showToast("Password reset. Please sign in with your new password.", "success");
      onDone();
    } catch (error) {
      showToast(
        describeError(error, "Couldn't reset your password. Please try again."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  if (step === "request") {
    return (
      <>
        <span className="cl-kicker cl-kicker-form"><span className="cl-dot" /> Reset password</span>
        <h2>Forgot password?</h2>
        <p className="cl-flow-sub">
          Enter the email on your account and we&apos;ll send you a
          verification code.
        </p>

        <label className="cl-label" htmlFor="cl-forgot-email">
          Email address
        </label>
        <input
          id="cl-forgot-email"
          className="cl-input"
          type="text"
          placeholder="you@example.com"
          autoFocus
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleRequestOtp()}
        />

        <button
          className={`cl-btn cl-btn-primary cl-submit${loading ? " loading" : ""}`}
          onClick={handleRequestOtp}
          disabled={loading}
        >
          {loading ? <span className="cl-loader" /> : "Send code"}
        </button>

        <p className="cl-apply">
          <button type="button" className="cl-link-btn" onClick={onCancel}>
            Back to sign in
          </button>
        </p>
      </>
    );
  }

  if (step === "verify") {
    return (
      <>
        <span className="cl-kicker cl-kicker-form"><span className="cl-dot" /> Reset password</span>
        <h2>Enter the code</h2>
        <p className="cl-flow-sub">
          We sent a verification code to <strong>{email}</strong>.
        </p>

        <label className="cl-label" htmlFor="cl-otp">
          Verification code
        </label>
        <input
          id="cl-otp"
          className="cl-input"
          type="text"
          inputMode="numeric"
          placeholder="123456"
          autoFocus
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
        />

        <button
          className={`cl-btn cl-btn-primary cl-submit${loading ? " loading" : ""}`}
          onClick={handleVerifyOtp}
          disabled={loading}
        >
          {loading ? <span className="cl-loader" /> : "Verify code"}
        </button>

        <p className="cl-apply">
          <button
            type="button"
            className="cl-link-btn"
            onClick={() => setStep("request")}
          >
            Use a different email
          </button>
        </p>
      </>
    );
  }

  return (
    <>
      <span className="cl-kicker cl-kicker-form"><span className="cl-dot" /> Reset password</span>
      <h2>Set a new password</h2>
      <p className="cl-flow-sub">
        Choose a new password for your account. Finish this within 5 minutes
        of receiving your code.
      </p>

      <label className="cl-label" htmlFor="cl-new-password">
        New password
      </label>
      <div className="cl-password-wrap">
        <input
          id="cl-new-password"
          className="cl-input"
          type={showPassword ? "text" : "password"}
          autoFocus
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
        />
        <span
          className="cl-eye"
          onClick={() => setShowPassword((prev) => !prev)}
        >
          {showPassword ? <FaEyeSlash /> : <FaEye />}
        </span>
      </div>

      <label className="cl-label" htmlFor="cl-confirm-password">
        Confirm new password
      </label>
      <input
        id="cl-confirm-password"
        className="cl-input"
        type={showPassword ? "text" : "password"}
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
      />

      <button
        className={`cl-btn cl-btn-primary cl-submit${loading ? " loading" : ""}`}
        onClick={handleResetPassword}
        disabled={loading}
      >
        {loading ? <span className="cl-loader" /> : "Reset password"}
      </button>

      <p className="cl-apply">
        <button
          type="button"
          className="cl-link-btn"
          onClick={() => setStep("verify")}
        >
          Back
        </button>
      </p>
    </>
  );
}

function ContactSupportForm({ onDone }) {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    category: "",
    message: "",
    website: "", // honeypot, hidden from people
  });
  const [files, setFiles] = useState([]); // [{ id, file }]
  const [attachError, setAttachError] = useState("");
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [sentMessage, setSentMessage] = useState("");

  const addFiles = (incoming) => {
    let error = "";
    const accepted = [];
    for (const file of Array.from(incoming || [])) {
      const maxMb = isVideo(file) ? ATTACH_VIDEO_MAX_MB : ATTACH_MAX_MB;
      if (!ATTACH_TYPES.includes(file.type)) {
        error = `${file.name}: only JPG, PNG, PDF, MP4, MOV or WEBM files`;
      } else if (file.size > maxMb * 1024 * 1024) {
        error = `${file.name} is larger than ${maxMb}MB`;
      } else if (files.length + accepted.length >= ATTACH_MAX_FILES) {
        error = `You can attach up to ${ATTACH_MAX_FILES} files`;
      } else {
        accepted.push({ id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`, file });
      }
    }
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
    setAttachError(error);
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setAttachError("");
  };

  // A screenshot pasted anywhere in the form (Cmd/Ctrl+V) is attached.
  const handlePaste = (e) => {
    const pasted = Array.from(e.clipboardData?.files || []);
    if (!pasted.length) return;
    e.preventDefault();
    addFiles(pasted);
  };

  useEffect(() => {
    let alive = true;
    creatorPublicSupportCategories()
      .then((res) => {
        const list = res?.data?.data;
        if (alive && Array.isArray(list)) setCategories(list);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      showToast("Please enter your name and email.", "warning");
      return;
    }
    if (!form.category) {
      showToast("Please choose what this is about.", "warning");
      return;
    }
    if (form.message.trim().length < 10) {
      showToast("Please add a message of at least 10 characters.", "warning");
      return;
    }
    try {
      setLoading(true);
      // No subject field on this form; the API still requires one, so the
      // chosen topic's label stands in for it.
      const topic = categories.find((c) => c.key === form.category);
      const body = new FormData();
      Object.entries(form).forEach(([key, value]) => body.append(key, value));
      body.append("subject", topic?.label || form.category);
      body.append("page", "creator-login");
      files.forEach((f) => body.append("attachments", f.file));
      const response = await creatorGuestSupportRequest(body);
      setSentMessage(
        response?.data?.message ||
          "Your message has been sent. We'll reply by email."
      );
    } catch (error) {
      const attachmentError = error?.response?.data?.errors?.attachments?.[0];
      if (attachmentError) setAttachError(attachmentError);
      showToast(
        describeError(error, "Couldn't send your message. Please try again."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  if (sentMessage) {
    return (
      <>
        <span className="cl-kicker cl-kicker-form">Contact support</span>
        <h2>Message sent</h2>
        <p className="cl-flow-sub">{sentMessage}</p>
        <button className="cl-btn cl-btn-primary cl-submit" onClick={onDone}>
          Back to sign in
        </button>
      </>
    );
  }

  return (
    <div onPaste={handlePaste}>
      <span className="cl-kicker cl-kicker-form">Contact support</span>
      <h2>How can we help?</h2>
      <p className="cl-flow-sub">
        Can&apos;t sign in or stuck on something? Send us a message and our
        team will reply by email.
      </p>

      <label className="cl-label" htmlFor="cl-sup-name">
        Your name
      </label>
      <input
        id="cl-sup-name"
        className="cl-input"
        type="text"
        name="name"
        autoFocus
        autoComplete="name"
        maxLength={255}
        value={form.name}
        onChange={handleChange}
      />

      <label className="cl-label cl-field-gap" htmlFor="cl-sup-email">
        Email address
      </label>
      <input
        id="cl-sup-email"
        className="cl-input"
        type="email"
        name="email"
        placeholder="you@example.com"
        autoComplete="email"
        maxLength={255}
        value={form.email}
        onChange={handleChange}
      />

      <label className="cl-label cl-field-gap" htmlFor="cl-sup-category">
        What is this about?
      </label>
      <select
        id="cl-sup-category"
        className="cl-input cl-select"
        name="category"
        value={form.category}
        onChange={handleChange}
      >
        <option value="">Choose a topic</option>
        {categories.map((c) => (
          <option key={c.key} value={c.key}>
            {c.label}
          </option>
        ))}
      </select>

      {/* Same "Is this your problem?" answers as the signed-in Help & Support form */}
      <div className="cl-related">
        <RelatedFaqs key={form.category} items={faqsForTopic(form.category)} />
      </div>

      <label className="cl-label cl-field-gap" htmlFor="cl-sup-message">
        Message
      </label>
      <textarea
        id="cl-sup-message"
        className="cl-input cl-textarea"
        name="message"
        rows={5}
        maxLength={5000}
        placeholder="Tell us what happened and the email you sign in with."
        value={form.message}
        onChange={handleChange}
      />

      <label className="cl-label cl-field-gap" htmlFor="cl-sup-files">
        Screenshots or video <span className="cl-optional">(optional)</span>
      </label>
      {files.length < ATTACH_MAX_FILES && (
        <button
          type="button"
          className={`cl-dropzone${attachError ? " cl-dropzone-error" : ""}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (!loading) addFiles(e.dataTransfer.files);
          }}
          disabled={loading}
        >
          <strong>Add a screenshot or screen recording</strong>
          <span>
            Click, drag &amp; drop or paste. JPG, PNG or PDF up to {ATTACH_MAX_MB}MB,
            MP4, MOV or WEBM up to {ATTACH_VIDEO_MAX_MB}MB. Up to {ATTACH_MAX_FILES} files.
          </span>
        </button>
      )}
      <input
        id="cl-sup-files"
        ref={fileInputRef}
        type="file"
        multiple
        accept={ATTACH_TYPES.join(",")}
        hidden
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      {files.length > 0 && (
        <ul className="cl-file-list">
          {files.map((f) => (
            <li key={f.id} className="cl-file-item">
              <span className="cl-file-name">{f.file.name}</span>
              <span className="cl-file-size">{fmtSize(f.file.size)}</span>
              <button
                type="button"
                className="cl-file-remove"
                onClick={() => removeFile(f.id)}
                disabled={loading}
                aria-label={`Remove ${f.file.name}`}
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
      )}
      {attachError && <p className="cl-attach-error">{attachError}</p>}

      <input
        className="cl-honeypot"
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={form.website}
        onChange={handleChange}
      />

      <button
        className={`cl-btn cl-btn-primary cl-submit${loading ? " loading" : ""}`}
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? <span className="cl-loader" /> : "Send message"}
      </button>

      <p className="cl-apply">
        <button type="button" className="cl-link-btn" onClick={onDone}>
          Back to sign in
        </button>
      </p>
    </div>
  );
}

// "Contact Support" links elsewhere (admin login, Help Center, FAQ) point at
// /creator-login?contact=1 and expect the support form, not sign-in.
const wantsSupport = (search) =>
  new URLSearchParams(search).get("contact") === "1";

export default function CreatorLogin() {
  const location = useLocation();
  const [mode, setMode] = useState(() =>
    wantsSupport(location.search) ? "support" : "signin"
  ); // signin | forgot | support
  // Following one of those links while this page is already mounted only
  // changes the query string, so switch to the form then too.
  useEffect(() => {
    if (wantsSupport(location.search)) setMode("support");
  }, [location.search]);
  const { logo } = useTenant();
  const brandName = useBrandName();

  return (
    <div className="creator-login">
      {/* Dawn horizon: a low sun rising behind a curved horizon line */}
      <div className="cl-horizon" aria-hidden="true">
        <div className="cl-sun" />
        <div className="cl-arc" />
      </div>

      <header className="cl-top">
        <Link className="cl-brand" to="/creator-studio" aria-label={`${brandName} home`}>
          <img className="cl-logo" src={logo} alt={brandName} />
        </Link>
        <span className="cl-portal">
          <span className="cl-portal-dot" aria-hidden="true" />
          Creator &amp; Partner portal
        </span>
        <Link className="cl-back" to="/creator-studio">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 18l-6-6 6-6" /></svg>
          Back to {brandName}
        </Link>
      </header>

      <main className="cl-main">
        {/* Card with the four features floating around it (desktop) or as a
            chip row beneath it (smaller screens) */}
        <div className="cl-stage">
          <div className={`cl-card${mode === "support" ? " cl-card-wide" : ""}`}>
            {mode === "signin" && (
              <SignInForm onForgotPassword={() => setMode("forgot")} />
            )}
            {mode === "forgot" && (
              <ForgotPasswordFlow
                onDone={() => setMode("signin")}
                onCancel={() => setMode("signin")}
              />
            )}
            {/* Contact Support for a creator who can't sign in — same queue as
                the signed-in Help & Support form. */}
            {mode === "support" && (
              <ContactSupportForm onDone={() => setMode("signin")} />
            )}
          </div>

          <ul className="cl-perks">
            {PERKS.map((perk) => (
              <li className={`cl-perk ${perk.key}`} key={perk.key}>
                <span className="cl-perk-ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{perk.d}</svg>
                </span>
                <span>
                  <strong>{perk.title}</strong>
                  <span className="cl-perk-body">{perk.body}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </main>

      <footer className="cl-foot">
        <span>© {new Date().getFullYear()} {brandName}</span>
        <span className="cl-foot-sep" aria-hidden="true">·</span>
        <span>Earnings aren&apos;t guaranteed and depend on eligibility rules.</span>
        {mode !== "support" && (
          <>
            <span className="cl-foot-sep" aria-hidden="true">·</span>
            <button
              type="button"
              className="cl-link-btn"
              onClick={() => setMode("support")}
            >
              Contact Support
            </button>
          </>
        )}
      </footer>
    </div>
  );
}
